import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { default as Select, components } from "react-select";
import { formatTestId } from "../../util/EfupUtility";
import { alphanumericValidator } from "../../validators/InputValidator";
import { DropdownIndicator } from '../CustomDropdownIndicator/CustomDropdownIndicator'
import { DefaultAdvancedSearchOptions, VendorAdvancedSearchOptions } from '../../config/FilterOptionTypes'
import "./SearchFilter.css"

const customSelectStyles = {
    menu: (provided) => ({
        ...provided,
        marginTop: '5px'
    }),
    option: (styles, { isFocused }) => {
        return {
            ...styles,
            backgroundColor: isFocused ? '#00b5e2' : undefined,
            color: '#333333'
        }
    },
    control: (styles, { isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            boxShadow: isFocused ? '0 0 0 1px #00b5e2' : undefined,
            borderColor: isDisabled
                ? undefined
                : isSelected && !isFocused
                    ? '#00b5e2'
                    : isFocused
                        ? '#00b5e2'
                        : '#D3D6DE',
            ':hover': {
                borderColor: '#00b5e2',
            }
        }
    }
}

const Control = ({ children, ...props }) => {
    return (
        <components.Control {...props}>
            <div className="efup-filter-value-container" data-testid="search-filter-control-values">{children}</div>
        </components.Control>
    );
};

const Option = (props) => {
    const testId = formatTestId(`efup-search-filter-dropdown-${props.label}`)

    return (
        <components.Option {...props} className={`efup-filter-option efup-search-dropdown-option`}>
            <div data-testid={testId}>{props.label}</div>
        </components.Option>
    );
};

export const SearchFilter = forwardRef((props, ref) => {

    const [showSearch, setShowSearch] = useState(true);
    const [errors, setErrors] = useState("");
    const [searchType, setSearchType] = useState();
    const [searchValue, setSearchValue] = useState("");
    const searchInputRef = useRef(null);

    // Options
    const options = props.pageType === "vendor" ? VendorAdvancedSearchOptions : DefaultAdvancedSearchOptions

    // allow a parent to clear this filter
    useImperativeHandle(ref, () => ({
        reset() {
            setSearchValue("");
            setSearchType(options[0]);
            setErrors("");
        }
    }))

    useEffect(() => {
        setSearchType(options[0]);
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    const changePaymentType = (type) => {
        setSearchType(type);
    }

    const clearPaymentNumber = () => {
        setSearchValue(props.paymentNumber);
        setSearchType(options[0]);
        props.onChange('paymentNumber', '');
        setErrors('');
        searchInputRef.current.focus();
        props.enableApply();
    }
    const clearPartyName = () => {
        setSearchValue(props.partyName);
        setSearchType(options[1]);
        props.onChange('partyName', '');
        setErrors('');
        searchInputRef.current.focus();
        props.enableApply();
    }
    const clearDisbursementUuid = () => {
        setSearchValue(props.disbursementUuid);
        setSearchType(options[2]);
        props.onChange('disbursementUuid', '');
        setErrors('');
        searchInputRef.current.focus();
        props.enableApply();
    }

    const clearVendorCode = () => {
        setSearchValue(props.vendorCode);
        setSearchType(options[3]);
        props.onChange('vendorCode', '');
        setErrors('');
        searchInputRef.current.focus();
        props.enableApply();
    }

    const handleSearchRequest = () => {
        if (!searchType || !searchType.value || !searchValue) {
            return;
        }

        props.onChange(searchType.value, searchValue);
        setSearchValue("");
    }

    const handleInputChange = (value) => {
        let errorValue = alphanumericValidator(value);
        if (errorValue === "" && searchType.value && value) {
            let isUpdating = false;
            if (searchType.value === 'paymentNumber' && props.paymentNumber) {
                isUpdating = true;
                value = '';
            } else if (searchType.value === 'partyName' && props.partyName) {
                isUpdating = true;
                value = '';
            } else if (searchType.value === 'disbursementUuid' && props.disbursementUuid) {
                isUpdating = true;
                value = '';
            } else if (searchType.value === 'vendorCode' && props.vendorCode) {
                isUpdating = true;
                value = '';
            }

            if (isUpdating) {
                if (props.pageType === 'vendor') {
                    errorValue = "Cannot search more than one Vendor Name, Payment Number, Disbursement UUID, or Vendor ID simultaneously.";
                } else {
                    errorValue = "Cannot search more than one Party Name, Payment Number, or Disbursement UUID simultaneously.";
                }
            }
        }
        setErrors(errorValue);
        setSearchValue(value);
    }

    return <>
        {showSearch &&
            <div className="cb-row d-none d-lg-block search-filter" data-testid="efup-search-filter">

                {/* <!-- Filter Headers--> */}
                <div className="cb-row d-none d-lg-block d-flex">
                    <div className="cb-col-lg-8">
                        <div className="sectionTitle" data-testid="payment-filter-filter-payments-label">Search by</div>
                    </div>
                </div>

                <div className="cb-row d-none d-lg-block">

                    {/* dropdowns */}
                    <div className="cb-col-lg-12 d-flex">

                        <Select
                            className="search-filter-select"
                            onChange={changePaymentType}
                            value={searchType}
                            options={options}
                            classNamePrefix="search-filter-select"
                            isSearchable={false}
                            data-testid="efup-search-filter-dropdown"
                            customProps={{ selectLabel: "Amount:", selectType: "amount" }}
                            components={{
                                IndicatorSeparator: () => null,
                                Control,
                                Option,
                                DropdownIndicator
                            }}
                            styles={customSelectStyles}
                        />

                        <input className={"search-filter-input" + (errors ? ' cb-input-error search-filter-input-error' : '') + ' cb-form-control'}
                            ref={searchInputRef}
                            type="text"
                            value={searchValue}
                            maxLength={searchType?.maxLength}
                            onChange={e => handleInputChange(e.target.value)}
                            onBlur={e => handleInputChange(e.target.value)}
                            data-testid="efup-search-filter-query-input"
                        />
                        {!errors &&
                            <Link
                                to=""
                                onClick={handleSearchRequest}
                                className="search-filter-search-add"
                                data-testid="efup-search-filter-add">
                                <i className="cb-icon icon-add_circle"></i>
                            </Link>
                        }
                    </div>
                    {errors &&
                        <div className="cb-col-lg-12 d-flex cb-input-error search-filter-errors" data-testid='search-filter-errors'>{errors}</div>
                    }

                </div>
            </div>
        }

        {/* Buttons */}
        <div className="cb-col-lg-12 d-flex search-filter-bubbles">
            {props.paymentNumber &&
                <button className="search-filter-bubble" data-testid="efup-search-bubble-payment-number">
                    <i
                        className="cb-icon icon-close"
                        alt="Close Button"
                        onClick={clearPaymentNumber}
                        data-testid="efup-search-bubble-payment-number-close-button"
                    />
                    Payment Number - {props.paymentNumber}</button>
            }
            {props.partyName &&
                <button className="search-filter-bubble" data-testid="efup-search-bubble-party-name">
                    <i
                        className="cb-icon icon-close"
                        alt="Close Button"
                        onClick={clearPartyName}
                        data-testid={`efup-search-bubble-${props.pageType === 'vendor' ? "vendor-name" : "party-name"}-close-button`}
                    />{props.pageType === 'vendor' ? "Vendor Name - " : "Party Name - "}{props.partyName}</button>
            }
            {props.disbursementUuid &&
                <button className="search-filter-bubble" data-testid="efup-search-bubble-uuid">
                    <i
                        className="cb-icon icon-close"
                        alt="Close Button"
                        onClick={clearDisbursementUuid}
                        data-testid="efup-search-bubble-uuid-close-button"
                    />
                    Disbursement UUID - {props.disbursementUuid}</button>
            }
            {props.pageType === 'vendor' && props.vendorCode &&
                <button className="search-filter-bubble" data-testid="efup-search-bubble-uuid">
                    <i
                        className="cb-icon icon-close"
                        alt="Close Button"
                        onClick={clearVendorCode}
                        data-testid="efup-search-bubble-vendor-id-close-button"
                    />
                    Vendor ID - {props.vendorCode}</button>
            }
        </div>

        {/* Advanced Search Toggle */}
        {showSearch ?
            <Link to="" onClick={() => setShowSearch(false)} className="search-filter-toggle" data-testid="efup-search-filter-hide">Hide Advanced Search</Link>
            :
            <Link to="" onClick={() => setShowSearch(true)} className="search-filter-toggle" data-testid="efup-search-filter-show">Show Advanced Search</Link>
        }

    </>;
})
